import moment from 'moment';
import type { Timestamp } from '@firebase/firestore';

import type { GroupedTimelineItems, TimelineItem } from './HistoryDialog.types.ts';

export const formatDate = (timestamp: Timestamp | null): string => {
  if (!timestamp) {
    return 'No date';
  }

  const date = timestamp.toDate();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const itemDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (itemDate.getTime() === today.getTime()) {
    return 'Today';
  }

  if (itemDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatTime = (timestamp: Timestamp | null): string => {
  if (!timestamp) {
    return '';
  }

  return timestamp.toDate().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const sortTimelineItemsWithinGroup = (a: TimelineItem, b: TimelineItem): number => {
  // If both items have no actionedAt, return 0 (equal)
  if (!a.actionedAt && !b.actionedAt) {
    return 0;
  }

  // If a has no actionedAt, return 1 (b is newer)
  if (!a.actionedAt) {
    return 1;
  }

  // If b has no actionedAt, return -1 (a is newer)
  if (!b.actionedAt) {
    return -1;
  }

  /**
   * If both items have actionedAt, return the difference in milliseconds
   * If b is newer, return a positive number
   * If a is newer, return a negative number
   * If they are the same, return 0
   */
  return b.actionedAt.toMillis() - a.actionedAt.toMillis();
};

export const groupTimelineItemsByDate = (items: TimelineItem[]): GroupedTimelineItems[] => {
  const groupedMap = items.reduce(
    (acc, item) => {
      const dateKey = formatDate(item.actionedAt);

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push(item);

      return acc;
    },
    {} as Record<string, TimelineItem[]>,
  );

  // Convert groupedMap to array of GroupedTimelineItems
  const grouped: GroupedTimelineItems[] = Object.entries(groupedMap).map(([date, groupItems]) => ({
    date,
    relativeDate: moment(groupItems[0]?.actionedAt?.toDate()).fromNow(),
    items: groupItems.toSorted(sortTimelineItemsWithinGroup),
  }));

  return grouped;
};
